import {registerBrick} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core'
import React, {useRef} from "react";
import ChartAuto from 'chart.js/auto';
import {combineLatest} from "rxjs";

export default class Chart extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('data'),
            $.observe('type'),
            $.observe('config')
        ]);
    }
    /**
     * @override
     * @param {!BrickContext} $
     */
    static getReactComponent($) {
        return () => {
            const rawData = useProperty($, 'data');
            const chartConfig = useProperty($, 'config');
            const type = useProperty($, 'type');
            const canvasRef = useRef(null);

            React.useEffect(()=>{
                let canvas = canvasRef.current;
                // check if props are ready
                if (!canvas || !rawData || !chartConfig || !type) return;

                // destroy the existing chart before creating a new one
                if (canvas.chart) {
                    canvas.chart.destroy();
                }

                const config = Chart.mapConfig(chartConfig.toObject(true));
                const data = Chart.mapDataToChart(rawData, chartConfig.toObject(true).label, chartConfig.toObject(true).borderWidth);

                new ChartAuto(canvas, {
                    type: type,
                    data: data,
                    options: config,
                });
            }, [rawData, chartConfig, type]);

            return <div style={{ height: '100%', width: '100%' }}><canvas ref={canvasRef}></canvas></div>
        }
    }
    /**
     * @override
     * @param {(CloudObject[])} dataObject
     * @param {(string)} label
     * @param {(number)} borderWidth
     */
    static mapDataToChart(dataObject, label, borderWidth) {
        const cloudObj = dataObject.map((item) => item.toObject(true));

        const labels = cloudObj.map(item => item.label);
        const dataValues = cloudObj.map(item => item.number);
        const colors = cloudObj.map(item => {
            // Check if the color is either #000000, #00000000, rgba(0,0,0,0), or rgb(0, 0, 0, 0)
            const isNotGoodColor = /^#000000$|^#00000000$/i.test(item.color) || /^rgba?\(0, ?0, ?0, ?0\)$/i.test(item.color);

            return isNotGoodColor ? null : item.color ? item.color.toHexString().toLowerCase() : null;
        });
        // filter the nulls so it uses default colors
        const filteredColors = colors.filter(item => item !== null);

        // UNUSED FOR NOW
        // make new color array, same colors but a bit darker for hover
        //const hoverColors = Chart.darkenColors(filteredColors);
        // Maybe could have the possibility to have different hover colors to the preset, seems unecessary tho.

        return {
            labels: labels,
            datasets: [{
                ...(label && { label: label}),
                data: dataValues,
                borderWidth: borderWidth,
                ...((filteredColors.length === dataValues.length) && { backgroundColor: filteredColors}),
                //...((hoverColors.length === dataValues.length) && {pointBackgroundColor: hoverColors}),
            }]
        };
    }
    /**
     * @override
     * @param {(CloudObject[])} dataObject
     */
    static mapData = (dataObject) => {
        return dataObject.map((item) => item.toObject(true));
    }
    /**
     * @override
     * @param {(object)} configObject
     */
    static mapConfig = (configObject) => {
        // map config data into correct format supported by Chart.js
        const baseConfig = {
            plugins: {
                title:{
                    display: configObject.titleDisplay,
                    text: configObject.titleText,
                },
                tooltip: {
                    enabled: configObject.tooltipEnabled,
                    mode: configObject.tooltipMode,
                },
                legend: {
                    display: configObject.legendDisplay,
                    position: configObject.legendPosition,
                },
            },
            hover:{
              mode: 'nearest',
            },
            maintainAspectRatio: configObject.maintainAspectRatio,
            responsive: configObject.responsive,
            x: {
                ...(configObject.xMin && { min: configObject.xMin }),
                ...(configObject.xMax && { max: configObject.xMax }),
            },
            y: {
                ...(configObject.yMin && { min: configObject.yMin }),
                ...(configObject.yMax && { max: configObject.yMax }),
            },
        }

        return {
            ...baseConfig,
            ...(configObject.configJson ? configObject.configJson : {}),
        };
    }
    /*
    // darken color function: maybe will use later
    static darkenColors(colors) {
        return colors.map(color => Chart.darkenColor(color, 30));
    }

    static darkenColor(rgbColor, percent) {
        const [r, g, b, a] = rgbColor.toRGBString().match(/\d+/g).map(Number);

        // darken color by percent
        const darkenedR = Math.round(r * (1 - percent / 100));
        const darkenedG = Math.round(g * (1 - percent / 100));
        const darkenedB = Math.round(b * (1 - percent / 100));

        return `rgba(${darkenedR}, ${darkenedG}, ${darkenedB}, ${a})`;
    }*/
};

registerBrick('018cf2b078055a8edaca', Chart);
